import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLocale } from '../../application/i18n/LocaleProvider';
import useFocusReset from '../../application/motion/useFocusReset';
import useScrollReset from '../../application/motion/useScrollReset';
import useScrollReveal from '../../application/motion/useScrollReveal';
import useCardGestures from '../../application/motion/useCardGestures';
import { useGarden } from '../../application/plants/useGarden';
import { usePlantCatalogue } from '../../application/plants/usePlantCatalogue';
import usePlantDeck from '../../application/plants/usePlantDeck';
import { findPlantById, plantTagline } from '../../domain/plants/plantCatalogue';
import { findCategory } from '../../domain/plants/plantCategories';
import { deckSeats, deckStep } from '../../domain/plants/plantDeck';
import { LeafGlyph } from '../chrome/BrandIcons';
import CompanionRail from '../plants/CompanionRail';
import PlantCardAction from '../plants/PlantCardAction';
import DecisionPlate from '../plants/DecisionPlate';
import PlantBrief from '../plants/PlantBrief';
import PlantDeckControls from '../plants/PlantDeckControls';
import PlantDeckPreload from '../plants/PlantDeckPreload';
import PlantDeckHint from '../plants/PlantDeckHint';
import PlantHero from '../plants/PlantHero';
import PlantShareSheet from '../plants/PlantShareSheet';
import { BackIcon } from '../icons/ControlIcons';
import { PlantCareTipsPanel, PlantLede } from '../plants/PlantProsePanels';
import SharedArrivalBand from '../plants/SharedArrivalBand';

/**
 * One plant, in full (FR-06), composed to
 * `documentation/starter/detail-page-reference.png` — hero, decision plate, the
 * two prose disclosures, the companion rail and one primary action.
 *
 * The first screen is a stage of two parts (SPEC-024): the picture on top, the
 * brief — name, botanical name, category, the one line that says what it is
 * like, and the four decisions in short — on paper under it. Below the fold the
 * page reads in one order and answers one question at a time: the lede, the
 * details, care tips, and the plants that go beside it.
 *
 * Since SPEC-025 that first screen is a CARD, and the page has two states.
 *
 * COLLAPSED, the card is the whole of the screen: the picture, the brief and the
 * one action, rounded and standing on the canvas, with the rest of its category
 * stacked behind it. Nothing below it is rendered — there is nothing to scroll,
 * which is what frees the vertical axis to mean "open this".
 *
 * EXPANDED, the card gives up its margins and its corners and the page is
 * exactly the page SPEC-024 composed, in the order SPEC-024 D-10 set. That is
 * deliberate: the whole plant is not a new screen, it is this one opened, and
 * every band of it is the band it always was.
 *
 * The state is screen state and not a route. A shared link opens the CARD — the
 * invitation — rather than a page somebody else had scrolled, and the back
 * button leaves the plant instead of unwinding a disclosure (SPEC-025 D-3).
 *
 * Everything on this page comes from the plant's content pack, so adding the
 * 23rd plant gives it a detail page for free, which is the whole point of FR-07.
 * This screen is the composition; each band is its own component under
 * `ui/plants/` (SPEC-003).
 */

/**
 * The way out of a plant, floating over whatever the reader has scrolled to.
 *
 * It belongs to the page rather than to the hero (where the reference draws it,
 * and where it lived until now) for one reason: the hero clips to its own
 * bounds, which makes it a scroll container, and a sticky child of a box that
 * never scrolls never sticks. Out here its containing block is the whole
 * article, so it survives the artwork leaving the screen.
 */
function BackToAll() {
    const { t, localeCode } = useLocale();

    return (
        <div className="plant-detail__back">
            <Link
                className="round-button"
                to={`/${localeCode}/plants`}
                aria-label={t('plants.backToAll')}
            >
                <BackIcon size={22} />
            </Link>
        </div>
    );
}

/**
 * How much of the top of the scrollport the way back is standing in.
 *
 * `.plant-detail__back` is sticky and zero-height, so it takes no room in the
 * flow and floats over whatever is under it. Anything scrolled flush to the top
 * of the viewport therefore arrives UNDERNEATH it — which put the description a
 * reader had just been carried to behind a round button (D-38).
 *
 * Measured rather than restated as a sum of the tokens that position it: the
 * button's own bottom edge, plus a little air, is the number that stays true when
 * one of those tokens changes.
 */
function backClearance() {
    if (typeof document === 'undefined') {
        return 0;
    }

    const button = document.querySelector('.plant-detail__back .round-button');
    const scroller = document.querySelector('.app-shell__content');

    if (!button || !scroller) {
        return 0;
    }

    return Math.max(0, button.getBoundingClientRect().bottom - scroller.getBoundingClientRect().top) + 12;
}

/**
 * Is the opened page already scrolled to its top?
 *
 * The shell's scrollport, asked directly rather than mirrored into React state:
 * this is read once per gesture, on a pointer press, and a scroll listener that
 * re-rendered the card on every frame of every scroll would be a large price for
 * one boolean nobody looks at in between.
 */
function atTopOfPage() {
    const scroller = document.querySelector('.app-shell__content');

    return !scroller || scroller.scrollTop <= 0;
}

export default function PlantDetailScreen() {
    const { plantId } = useParams();
    const { t, localeCode, languageCode } = useLocale();
    const { status, plants } = usePlantCatalogue();
    const { isInGarden, toggle } = useGarden();
    const [shareResult, setShareResult] = useState(null);
    // The share sheet belongs to the SCREEN, not to the band: two things open it
    // now — the glyph over the picture and the card's primary action (D-15) —
    // and a sheet owned by one of them would be a sheet the other cannot reach.
    const [isShareOpen, setShareOpen] = useState(false);
    const [isExpanded, setExpanded] = useState(false);
    // Which way the card in front is leaving, while it is leaving (D-12).
    const careTipsId = useId();
    const bodyRef = useRef(null);
    const bodyId = useId();
    const plant = findPlantById(plants, plantId);

    // Which plant a swipe deals, and whether the card leaves before it does.
    const { deal, deck, dealing, step } = usePlantDeck({
        plants, plantId, localeCode, languageCode,
    });

    // A swipe lands on a plant the reader has not scrolled — including one they
    // swiped for from two thirds of the way down the last page (D-6). The
    // companion rail's taps have had this bug since SPEC-003.
    useScrollReset(plantId);
    // A swipe deals a CARD. Landing on the next plant already opened would make
    // the deck a page-turner through five long pages rather than a browse.
    useEffect(() => setExpanded(false), [plantId]);
    // And the caret goes with the page: the two arriving in different places is
    // the same bug for anyone reading by keyboard or by ear (D-6).
    const pageRef = useFocusReset(plantId);
    // Opening the card leaves the first screen exactly where it was (D-19), so
    // without this a lift discloses something the reader cannot see. The page
    // follows the gesture up to it (D-21).
    useScrollReveal(isExpanded, bodyRef, { clearance: backClearance });

    // A lift opens the card and a pull down closes it (SPEC-025 D-7). Which of
    // the two a vertical gesture is allowed to BE is decided at pointer-down by
    // `atTopOfPage`, so a drag that begins mid-page is the reader's scrolling
    // and this never sees it.
    const lift = useCallback((direction) => setExpanded(direction === 'up'), []);

    // Collapsed, there is nothing under the card to scroll and vertical is the
    // lift. Opened, vertical is the reader's scrolling — EXCEPT from the very top
    // of the page, where there is nothing above to scroll to and a pull down is
    // the reader closing the card.
    //
    // A cut of this also let the HANDLE close the card from anywhere, on the
    // reasoning that it is the card's own control surface. It cannot: opening
    // scrolls the reader to the plant (D-21) and the handle goes off the top of
    // the screen with the rest of the brief, so the rule described a gesture
    // nobody could make. Scrolling back up is how a reader reaches the close, and
    // it is what they are already doing.
    //
    // Asked when the finger lands rather than per frame, so a gesture cannot
    // change its mind halfway.
    const { handlers, gesture } = useCardGestures({
        onStep: step,
        onLift: lift,
        verticalPolicy: () => (!isExpanded || atTopOfPage() ? 'lift' : 'scroll'),
    });

    if (status === 'loading') {
        return <p className="plants-screen__status">{t('plants.loading')}</p>;
    }

    // An unknown id is a mistyped or retired URL, not a crash. The catalogue is
    // content, so its shape changes without a deploy — this has to stay a
    // designed state rather than an error boundary.
    if (!plant) {
        return (
            <section className="plant-detail plant-detail--missing">
                <div className="empty-state">
                    <LeafGlyph className="empty-state__mark" size={32} />
                    <p className="empty-state__message">{t('plants.notFound')}</p>
                    <Link className="button button--quiet" to={`/${localeCode}/plants`}>
                        {t('plants.backToAll')}
                    </Link>
                </div>
            </section>
        );
    }

    const saved = isInGarden(plant.id);
    const category = findCategory(deck.categoryKey);
    // How far the card has been dragged, and which way, as ONE custom property
    // per frame on ONE element (D-5). The card's translate and tilt and the
    // stack's rise are all CSS expressions over it.
    //
    // Damped to a third where there is nothing to go to. That used to be the
    // rubber-band at a category's edge; the deck is a ring now, so the only
    // place it is left is a category of one — where a card that answered a drag
    // by moving and then not changing would be lying about what it did.
    const swiping = gesture?.axis === 'horizontal' ? gesture : null;
    const shift = swiping && !deckStep(deck, swiping.direction) ? 1 / 3 : 1;
    // While a card is being dealt away, `--deck-shift` is the direction it went
    // rather than the direction a finger is pointing — the finger has gone.
    const leaving = dealing ?? swiping?.direction;
    const seats = deckSeats(deck);
    const deckStyle = {
        '--plant-wash': `var(--wash-${plant.wash})`,
        '--deck-progress': swiping ? swiping.progress * shift : 0,
        '--deck-shift': leaving === 'forward' ? -1 : 1,
        '--deck-deal': `${deal}ms`,
    };

    return (
        <article
            className={[
                'plant-detail',
                isExpanded ? 'plant-detail--expanded' : 'plant-detail--collapsed',
                swiping ? 'plant-detail--swiping' : '',
                dealing ? 'plant-detail--dealing' : '',
                dealing ? `plant-detail--dealing-${dealing}` : '',
            ].filter(Boolean).join(' ')}
            style={deckStyle}
            // Where focus lands when a swipe or a companion tap brings a new
            // plant in. The container rather than its heading, because the way
            // back and the picture rail come BEFORE the name in the document,
            // and a reader put on the heading would have them behind them.
            ref={pageRef}
            tabIndex={-1}
            {...handlers}
        >
            {/* Every other route change follows an activated link, which a screen
                reader announces. A swipe activates nothing, so without this the
                page silently becomes a different plant (D-7). */}
            <p className="plant-detail__now-showing visually-hidden" role="status">
                {t('plants.detail.nowShowing', { plant: plant.prose?.commonName ?? plant.id })}
            </p>
            <BackToAll />
            {/* One screen's worth, and the only place on this page where a
                height is declared: the brief takes what its type needs and the
                hero takes the rest, so the picture ends where the words begin in
                every language (SPEC-024 D-1). */}
            {/* Named and counted, because a deck browsed by dragging says
                nothing at all to a reader who is not looking at it (D-10). */}
            <div
                className="plant-deck"
                role="group"
                aria-label={t('plants.detail.deckPosition', {
                    category: category ? t(category.messageKey) : '',
                    index: deck.index + 1,
                    total: deck.total,
                })}
            >
                <PlantDeckControls deck={deck} />
                {/* Said once per device, on the first plant a reader opens, and
                    never again (D-18). The stack underneath says there is more of
                    this category; only this says how to reach it. */}
                <PlantDeckHint dismissed={Boolean(swiping || dealing)} />
                {/* The slot the top card sits in, and the frame the cards behind
                    it are measured against. It does NOT move: the card is what a
                    drag translates, and a stack that travelled with the card it
                    stands behind would be one object sliding rather than a card
                    being dealt off a deck (D-12). */}
                <div className="plant-deck__slot">
                    <PlantDeckPreload deck={deck} />
                    {/* ONE KEYED LIST, and every plant that can be on screen in
                        the next moment already has a seat (D-29).

                        This replaced a front card and a separate stack behind it.
                        Two kinds of element meant a step rewrote the front band's
                        `src`, which tears a live <img>'s frame down and
                        re-attaches its bitmap — the repaint every decision from
                        D-23 to D-28 was working around. Keyed by plant, a step
                        MOVES the node that was behind into the front role, so the
                        picture already on screen is the picture that stays.

                        Painting order is `--seat-depth`, not document order: the
                        card in hand is not last in the list, so where a seat sits
                        says nothing about which card it is. */}
                    {(isExpanded ? seats.slice(0, 1) : seats).map((seat) => (
                        <section
                            key={seat.plant.id}
                            className={`plant-deck__seat plant-deck__seat--${seat.role}`}
                            style={{
                                '--seat-depth': seat.seatDepth,
                                '--plant-wash': `var(--wash-${seat.plant.wash})`,
                            }}
                            aria-hidden={seat.role === 'front' ? undefined : 'true'}
                            inert={seat.role === 'front' ? undefined : ''}
                        >
                            <div className="plant-detail__stage">
                                {/* A peek draws no picture: it is the edge that
                                    says there is a deck here, and a fourth decoded
                                    band for a few pixels of card is a cost with
                                    nothing on the other side. */}
                                {seat.role === 'peek' ? null : (
                                    <PlantHero
                                        plant={seat.plant}
                                        isFront={seat.role === 'front'}
                                        isSaved={saved}
                                        onToggleSaved={() => toggle(plant.id)}
                                        onShare={() => setShareOpen(true)}
                                    />
                                )}
                                {/* ONLY THE PICTURE is on the card behind. The words,
                                    the glances and the action belong to the card in
                                    HAND, and they arrive with the reveal after it
                                    lands (D-37).

                                    They were on every seated card for two rounds, so
                                    that nothing mounted at the swap. What that bought
                                    in DOM stability it lost in what a reader sees: the
                                    card behind was already a finished plant, and
                                    promoting it replaced one populated layer with
                                    another almost exactly like it. That is the flicker,
                                    described by the owner as "one layer replaced with
                                    another, having very similar content".

                                    The ILLUSTRATION still never moves and is never
                                    rebuilt — it is the same node throughout (D-29), and
                                    that is the part which must not change. Everything
                                    printed on the card is meant to arrive, and now
                                    visibly does. */}
                                {seat.role !== 'front' ? null : (
                                    <div
                                        className="plant-deck__paper"
                                        key={seat.plant.id}
                                    >
                                        <PlantBrief
                                            plant={seat.plant}
                                            isExpanded={seat.role === 'front' && isExpanded}
                                            onToggle={seat.role === 'front'
                                                ? () => setExpanded((open) => !open)
                                                : undefined}
                                            bodyId={seat.role === 'front' ? bodyId : undefined}
                                        />
                                    </div>
                                )}
                            </div>
                            {/* The body's BOX is on every seated card, so that
                                nothing at all is inserted when a seat is promoted
                                (D-36). Its contents are the card in hand's alone,
                                and only once opened. */}
                            {seat.role !== 'front' ? null : (
                                <>
                                {/* The whole plant is not a second screen — it is this
                                    card opened, in the order SPEC-024 D-10 set. Rendered
                                    only when it is open, which is what leaves the
                                    collapsed card with nothing to scroll and the vertical
                                    axis free to mean "open this" (D-7). */}
                                {/* Its CONTENTS mount when the card is opened, not
                                    when a seat is promoted. Built at promotion —
                                    hidden, but built — the arrival band, the lede,
                                    the details, the care tips and the companion
                                    rail all landed in the DOM at the moment the
                                    deck advanced, and the card was laid out and
                                    rasterised again around them (D-36). */}
                                <div
                                    className="plant-deck__body"
                                    id={seat.role === 'front' ? bodyId : undefined}
                                    ref={seat.role === 'front' ? bodyRef : undefined}
                                    hidden={seat.role !== 'front' || !isExpanded}
                                >
                                    {seat.role !== 'front' || !isExpanded ? null : (
                                    <>
                                    {/* Under the plant rather than over it (SPEC-019
                                        D-7). Somebody who followed a shared link came for
                                        the PLANT, and a green band above the artwork
                                        would put the product in front of the thing that
                                        was actually sent. It renders nothing at all for
                                        everyone else, which is almost everyone. */}
                                    <SharedArrivalBand plantId={plant.id} />
                                    {/* What this plant IS — as prose on the page rather
                                        than in a panel (SPEC-024 D-11) — then what it
                                        needs, then how to look after it. The details card
                                        sits between the two, so the page answers "what is
                                        it?" before "in what soil?", and the maintenance
                                        row's chevron points at the card below it. */}
                                    <PlantLede plant={plant} />
                                    <DecisionPlate plant={plant} careTipsId={careTipsId} />
                                    <PlantCareTipsPanel plant={plant} careTipsId={careTipsId} />
                                    <CompanionRail plant={plant} plants={plants} />
                                    </>
                                    )}
                                </div>
                                </>
                            )}
                            {/* The face's foot, on every seated card. A peek
                                draws no card at all, so it has none. */}
                            {seat.role !== 'front' ? null : (
                                    <PlantCardAction
                                        key={plant.id}
                                        onShare={() => setShareOpen(true)}
                                        shareResult={shareResult}
                                    />
                            )}
                        </section>
                    ))}
                </div>
                {isShareOpen ? (
                    <PlantShareSheet
                        plant={plant}
                        commonName={plant.prose?.commonName ?? plant.id}
                        tagline={plantTagline(plant.prose)}
                        onResult={setShareResult}
                        onClose={() => setShareOpen(false)}
                    />
                ) : null}
            </div>
        </article>
    );
}
